import {
  Box,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material"
import type React from "react"
import { extraColors } from "../../utils/theme"
import { NwssBubbleWeekTooltip } from "./NwssBubbleWeekTooltip"
import { nwssHeatmapTooltipSlotProps } from "./NwssDetectionHeatmapTooltip"
import { DrawerChartTitle } from "./precomputedDrawerStyles"
import {
  weekOverlapsNwssEventWindow,
  type NwssEventWindow,
} from "./nwssEventWindow"
import {
  formatNwssChartTitle,
  formatNwssResolvedLocation,
} from "./nwssDisplayFormat"
import type { NwssDrawerSampleScope } from "./nwssDrawerScope"
import type { NwssSignalSample } from "./nwssSampleTypes"
import {
  bubbleDetectionFillColor,
  bubbleDetectionStrokeColor,
  bubbleDiameterPx,
  bubbleTierFromCount,
  NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS,
  type NwssSiteBubbleTimelineData,
} from "./nwssWeeklyCharts"
import { NwssEventWindowLegend } from "./NwssEventWindowLegend"
import { NwssWeeklyChartFrame } from "./NwssWeeklyTimelineHeader"
import { NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX } from "./nwssWeeklyGridLayout"

export interface NwssSiteBubbleTimelineProps {
  timeline: NwssSiteBubbleTimelineData
  samples: NwssSignalSample[]
  disease?: string
  geo: string
  reportingCounties?: string[] | null
  truncated?: boolean
  eventWindow?: NwssEventWindow | null
  sampleScope?: NwssDrawerSampleScope
}

export const NwssSiteBubbleTimeline: React.FC<NwssSiteBubbleTimelineProps> = ({
  timeline,
  samples,
  disease,
  geo,
  reportingCounties = null,
  truncated = false,
  eventWindow = null,
  sampleScope = "historic",
}) => {
  const { sites, weekKeys, weekLabels, cells } = timeline
  if (sites.length === 0 || weekKeys.length === 0) return null

  const displayGeo: string = formatNwssResolvedLocation(geo) || geo
  const title = formatNwssChartTitle(
    disease,
    "weekly site detection timeline",
    displayGeo,
    sampleScope
  )

  return (
    <Box sx={{ width: "100%" }}>
      <DrawerChartTitle>{title}</DrawerChartTitle>
      <Typography
        variant="caption"
        sx={{ color: extraColors.translucent50, display: "block", mb: 1 }}
      >
        {`Last ${NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS} months · size and color = detections that week (0–5+).`}
        {truncated
          ? " Sample cap may omit recent context — weekly views can be incomplete."
          : ""}
      </Typography>
      {eventWindow && sampleScope === "historic" && (
        <NwssEventWindowLegend
          eventWindow={eventWindow}
          showOutsideBubbleGuide
        />
      )}
      <BubbleLegend />
      <Box sx={{ maxHeight: 280, overflow: "auto", width: "100%" }}>
        <BubbleGrid
          sites={sites}
          weekKeys={weekKeys}
          weekLabels={weekLabels}
          cells={cells}
          samples={samples}
          reportingCounties={reportingCounties}
          lookbackMonths={NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS}
          eventWindow={eventWindow}
          showEventWindowBand={
            sampleScope === "historic" && eventWindow != null
          }
        />
      </Box>
    </Box>
  )
}

interface BubbleGridProps {
  sites: string[]
  weekKeys: string[]
  weekLabels: string[]
  cells: Map<string, number>
  samples: NwssSignalSample[]
  reportingCounties: string[] | null
  lookbackMonths: number
  eventWindow: NwssEventWindow | null
  showEventWindowBand: boolean
}

const BubbleGrid: React.FC<BubbleGridProps> = ({
  sites,
  weekKeys,
  weekLabels,
  cells,
  samples,
  reportingCounties,
  lookbackMonths,
  eventWindow,
  showEventWindowBand,
}) => {
  const weekTrackSx: SxProps<Theme> = {
    flex: 1,
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: `repeat(${weekKeys.length}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${sites.length}, auto)`,
    gap: "2px",
    alignItems: "center",
  }

  return (
    <NwssWeeklyChartFrame
      weekKeys={weekKeys}
      eventWindow={eventWindow}
      showEventWindowBand={showEventWindowBand}
    >
      <Box sx={{ display: "flex", gap: "2px", width: "100%" }}>
        <Box
          sx={{
            width: NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX,
            flexShrink: 0,
            display: "grid",
            gridTemplateRows: `repeat(${sites.length}, auto)`,
            gap: "2px",
            alignItems: "center",
          }}
        >
          {sites.map(site => (
            <Typography
              key={site}
              sx={{
                fontSize: "0.65rem",
                color: extraColors.translucent50,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`Site ${site}`}
            >
              {site}
            </Typography>
          ))}
        </Box>
        <Box sx={weekTrackSx}>
          {sites.map(site =>
            weekKeys.map((week, index) => (
              <BubbleCell
                key={`${site}__${week}`}
                site={site}
                weekKey={week}
                weekLabel={weekLabels[index] ?? ""}
                count={cells.get(`${site}__${week}`) ?? 0}
                samples={samples}
                reportingCounties={reportingCounties}
                lookbackMonths={lookbackMonths}
                inEventWindow={weekOverlapsNwssEventWindow(week, eventWindow)}
              />
            ))
          )}
        </Box>
      </Box>
    </NwssWeeklyChartFrame>
  )
}

interface BubbleCellProps {
  site: string
  weekKey: string
  weekLabel: string
  count: number
  samples: NwssSignalSample[]
  reportingCounties: string[] | null
  lookbackMonths: number
  inEventWindow: boolean
}

const BubbleCell: React.FC<BubbleCellProps> = ({
  site,
  weekKey,
  weekLabel,
  count,
  samples,
  reportingCounties,
  lookbackMonths,
  inEventWindow,
}) => {
  const tier = bubbleTierFromCount(count)
  const size = bubbleDiameterPx(tier)
  const fill = bubbleDetectionFillColor(tier)
  const stroke = bubbleDetectionStrokeColor(tier)
  const outsideWindowOpacity = inEventWindow || tier === 0 ? 1 : 0.55

  return (
    <Tooltip
      arrow
      placement="top"
      slotProps={nwssHeatmapTooltipSlotProps}
      title={
        <NwssBubbleWeekTooltip
          row={site}
          weekKey={weekKey}
          weekLabel={weekLabel}
          groupBy="site"
          samples={samples}
          reportingCounties={reportingCounties}
          lookbackMonths={lookbackMonths}
          inEventWindow={inEventWindow}
        />
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          aspectRatio: "1",
          maxHeight: 22,
        }}
      >
        <Box
          component="span"
          sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            bgcolor: fill,
            border: `1.5px solid ${stroke}`,
            flexShrink: 0,
            opacity: outsideWindowOpacity,
          }}
        />
      </Box>
    </Tooltip>
  )
}

const LEGEND_TIERS = [0, 1, 2, 3, 4, 5] as const

const BubbleLegend: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "flex-end",
      gap: 1.25,
      mb: 0.75,
    }}
  >
    {LEGEND_TIERS.map(tier => (
      <Box
        key={tier}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Box
          sx={{
            width: bubbleDiameterPx(tier),
            height: bubbleDiameterPx(tier),
            borderRadius: "50%",
            bgcolor: bubbleDetectionFillColor(tier),
            border: `1.5px solid ${bubbleDetectionStrokeColor(tier)}`,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.5rem",
            color: extraColors.translucent50,
            mt: 0.25,
          }}
        >
          {tier === 5 ? "5+" : tier}
        </Typography>
      </Box>
    ))}
  </Box>
)
